// src/components/common/CategoryNav.tsx
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { categories } from '../../data/categories';
import { supportPrograms } from '../../data/supportPrograms';
import Icon from './Icon';

const CategoryNav: React.FC = () => {
    const [open, setOpen] = useState(false);
    const navRef = useRef<HTMLElement>(null);
    const location = useLocation();

    // 경로가 바뀌면 닫기
    useEffect(() => setOpen(false), [location.pathname, location.search]);

    // 바깥 클릭 / ESC 로 닫기
    useEffect(() => {
        if (!open) return;

        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    return (
        <nav className="gh-nav" aria-label="지원금 카테고리" ref={navRef}>
            <div className="gh-nav__inner">
                <button
                    type="button"
                    className={`gh-nav__all${open ? ' is-open' : ''}`}
                    onClick={() => setOpen(v => !v)}
                    aria-expanded={open}
                    aria-haspopup="true"
                >
                    <Icon name={open ? 'close' : 'grid'} size={16} />
                    전체 카테고리
                </button>

                {categories.map(c => (
                    <NavLink
                        key={c.id}
                        to={c.path}
                        end={c.path === '/'}
                        className={({ isActive }) => `gh-nav__link${isActive ? ' is-active' : ''}`}
                    >
                        {c.shortName}
                    </NavLink>
                ))}

                <NavLink
                    to="/2027"
                    className={({ isActive }) => `gh-nav__link${isActive ? ' is-active' : ''}`}
                >
                    2027 전망
                    <span className="gh-nav__badge">NEW</span>
                </NavLink>
            </div>

            {/* 스크롤되는 탭 영역(overflow) 밖에 두어야 잘리지 않습니다 */}
            {open && (
                <div className="gh-mega-wrap">
                    <div className="gh-mega-align">
                        <div className="gh-mega" role="menu">
                            <div className="gh-mega__grid">
                                {categories.slice(1).map(c => {
                                    const count = supportPrograms.filter(p => p.category === c.id).length;
                                    return (
                                        <Link key={c.id} to={c.path} className="gh-mega__item" role="menuitem">
                                            <span className="gh-mega__icon">
                                                <Icon name={c.icon} size={19} />
                                            </span>
                                            <span className="gh-mega__text">
                                                <span className="gh-mega__name">
                                                    {c.name}
                                                    <em>{count}</em>
                                                </span>
                                                <span className="gh-mega__desc">{c.tagline}</span>
                                            </span>
                                        </Link>
                                    );
                                })}
                            </div>

                            <div className="gh-mega__foot">
                                <Link to="/2026" className="gh-mega__link" role="menuitem">
                                    <Icon name="sparkle" size={15} />
                                    2026년 달라지는 정부지원금
                                </Link>
                                <Link to="/2027" className="gh-mega__link" role="menuitem">
                                    <Icon name="chart" size={15} />
                                    2027년 정부지원금 전망
                                </Link>
                                <Link to="/search" className="gh-mega__link" role="menuitem">
                                    <Icon name="search" size={15} />
                                    전체 지원금 {supportPrograms.length}건 보기
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default CategoryNav;
